import { Spin } from "antd";
import i18n from "i18next";
import { type FC, useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams } from "react-router";

import { useAcquirerCreatePhysicalToVirtualMutation } from "src/app/features/Acquirer/services/server/AcquirerApi";
import type { AcquirerToVirtualEntity } from "src/app/features/Acquirer/types/subTypes/AcquirerToVirtualEntity";
import { FormManager } from "../../../../../components/Form/FormManager";
import { useLoadFormDefinitionQuery } from "../../../../../components/Form/services/server/serverApi";
import { FormLayoutType } from "../../../../../components/Form/types";
import type { FormProps } from "../../../../../components/Form/types/FormProps";
import { ResourceName, ResourceVariant } from "../../../../../core/router/config/ResourceName";
import { BASE_PATH, FORM_ACTION } from "../../../../../core/router/types/routerTypes";
import { showSuccessNotification } from "../../../../../core/setup/redux/state/notifications/notificationsState";

export const AcquirerToVirtualCreateForm: FC<FormProps> = ({ runSubmit }) => {
    const { data: formDefinition, isFetching: configIsFetching } = useLoadFormDefinitionQuery("acquirers-to-virtual");

    const [formData, setFormData] = useState<AcquirerToVirtualEntity>({} as AcquirerToVirtualEntity);
    const onChangeCallback = useCallback(setFormData, [setFormData]);

    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();

    const [saveFormData, { isLoading: saveFormIsInProgress }] = useAcquirerCreatePhysicalToVirtualMutation();
    const onValidatedCallback = useCallback(
        async (valid: boolean) => {
            if (!valid || !formData) {
                return;
            }
            const result = await saveFormData({ data: formData });

            const hasError = "error" in result;
            if (!hasError) {
                const { data } = result;

                dispatch(
                    showSuccessNotification({
                        text: i18n.t("common.formSaveSuccess", {
                            id: data.physicalAcquirerId,
                            resource: "Acquirer to Virtual",
                        }),
                        autoCloseDisabled: false,
                    }),
                );

                const route = `${BASE_PATH}/${ResourceName.ACQUIRER}/${ResourceVariant.VIRTUAL}/${FORM_ACTION.EDIT}/${id}`;
                navigate(route);
            }
        },
        [saveFormData, formData, navigate, dispatch, id],
    );

    return (
        <Spin spinning={saveFormIsInProgress || configIsFetching}>
            {formDefinition?.formConfiguration && (
                <FormManager<AcquirerToVirtualEntity>
                    name="AcquirerVirtualCreateForm"
                    formData={{ ...formData, id }}
                    formLayout={FormLayoutType.TABS}
                    formDefinition={formDefinition.formConfiguration}
                    onChange={onChangeCallback}
                    shouldValidate={runSubmit}
                    onValidated={onValidatedCallback}
                />
            )}
        </Spin>
    );
};
